import React, { useState, useEffect } from 'react';
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';

const emptyGuid = '00000000-0000-0000-0000-000000000000';

const initialFieldValues = {
    categoryId: emptyGuid,
    name: '',
    imageFile: null,
    imageSrc: ''
};

const Category = () => {
    const [lists, setLists] = useState([]);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});

    const THEME_COLOR = '#065f46';

    useEffect(() => {
        GetData();
    }, []);

    // ================= API =================

    const GetData = async () => {
        try {
            const res = await axios.get(
                config.APIACTIVEURL + config.GETALLCATEGORIES
            );
            setLists(res.data || []);
        } catch (error) {
            console.error("Error fetching categories", error);
        }
    };

    // ================= FORM =================

    const handleInput = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    const showPreview = (e) => {
        if (e.target.files && e.target.files[0]) {
            const imageFile = e.target.files[0];
            const reader = new FileReader();
            reader.onload = (x) => {
                setValues({
                    ...values,
                    imageFile,
                    imageSrc: x.target.result
                });
            };
            reader.readAsDataURL(imageFile);
        }
    };

    const validate = () => {
        let temp = {};
        temp.name = values.name ? "" : "Category Name is required";

        // Image required ONLY when adding
        if (values.categoryId === emptyGuid) {
            temp.imageFile = values.imageFile ? "" : "Category Icon is required";
        }

        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    // ================= ADD / UPDATE =================

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setBtnLoading(true);

        const formData = new FormData();
        formData.append('categoryId', values.categoryId);
        formData.append('name', values.name);

        if (values.imageFile) {
            formData.append('imageFile', values.imageFile);
        }

        try {
            const isNew = values.categoryId === emptyGuid;

            if (isNew) {
                await axios.post(
                    config.APIACTIVEURL + config.CREATECATEGORY,
                    formData
                );
            } else {
                await axios.put(
                    config.APIACTIVEURL + config.UPDATECATEGORY,
                    formData
                );
            }

            setShowModal(false);
            resetForm();
            GetData();
        } catch (error) {
            console.error("Save failed", error);
        } finally {
            setBtnLoading(false);
        }
    };

    // ================= EDIT =================

    const handleEdit = (item) => {
        setValues({
            categoryId: item.categoryId,
            name: item.name,
            imageFile: null,
            imageSrc: item.imageSrc
        });
        setErrors({});
        setShowModal(true);
    };

    // ================= DELETE =================

    const handleDelete = async () => {
        try {
            await axios.delete(
                config.APIACTIVEURL + config.DELETECATEGORY + "/" + itemToDelete
            );
            setShowDeleteModal(false);
            GetData();
        } catch (error) {
            console.error("Delete failed", error);
        }
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
    };

    // ================= UI =================

    return (
        <div className="page-content" style={{ paddingTop: "80px" }}>
            <div className="container-fluid py-4">
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <h4 className="fw-bold mb-0" style={{ color: '#064e3b' }}>
                            Categories
                        </h4>
                        <button
                            className="btn text-white fw-bold px-4 shadow-sm"
                            style={{ backgroundColor: THEME_COLOR }}
                            onClick={() => { resetForm(); setShowModal(true); }}
                        >
                            <i className="ri-add-line me-1"></i> Add Category
                        </button>
                    </div>

                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr className="text-secondary small text-uppercase">
                                        <th style={{ width: '120px' }}>Icon</th>
                                        <th>Category Name</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lists.map((item) => (
                                        <tr key={item.categoryId}>
                                            <td>
                                                <img
                                                    src={item.imageSrc}
                                                    alt={item.name}
                                                    className="rounded-circle shadow-sm me-3"
                                                    style={{
                                                        width: '45px',
                                                        height: '45px',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </td>
                                            <td className="ps-4 fw-bold text-dark">
    {item.name}
</td>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-outline-primary border-0 me-2"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    <i className="ri-pencil-line fs-5"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger border-0"
                                                    onClick={() => {
                                                        setItemToDelete(item.categoryId);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    <i className="ri-delete-bin-line fs-5"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ADD / EDIT MODAL */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold">
                            {values.categoryId === emptyGuid ? "Add New Category" : "Edit Category"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="px-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4 text-center">
                                <div
                                    className="mx-auto mb-2"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        border: '2px dashed #ccc',
                                        borderRadius: '15px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {values.imageSrc ? (
                                        <img
                                            src={values.imageSrc}
                                            className="w-100 h-100"
                                            style={{ objectFit: 'cover' }}
                                            alt="preview"
                                        />
                                    ) : (
                                        <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                            <i className="ri-image-add-line fs-1"></i>
                                        </div>
                                    )}
                                </div>

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={showPreview}
                                    className={`form-control form-control-sm ${errors.imageFile ? 'is-invalid' : ''}`}
                                />
                                {errors.imageFile &&
                                    <div className="invalid-feedback">{errors.imageFile}</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-bold text-muted">
                                    Category Name
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={values.name}
                                    onChange={handleInput}
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    placeholder="e.g. Hair, Massage, Yoga"
                                />
                                {errors.name &&
                                    <div className="invalid-feedback">{errors.name}</div>}
                            </div>

                            <div className="modal-footer border-0 p-0 mt-4">
                                <button
                                    type="button"
                                    className="btn btn-light px-4 fw-bold"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={btnLoading}
                                    className="btn text-white px-5 fw-bold shadow-sm"
                                    style={{ backgroundColor: THEME_COLOR }}
                                >
                                    {btnLoading ? 'Saving...' : 'Save Category'}
                                </button>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* DELETE MODAL */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Category</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this category and its icon?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            No, Keep it
                        </Button>
                        <Button variant="danger" onClick={handleDelete}>
                            Yes, Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default Category;
